import { createReducer, on } from '@ngrx/store';
import * as IngredientActions from './ingredient.actions';
import { Ingredient, IngredientBrief } from '../../../shared/models/ingredient.model';
import { IngredientCategory } from '../../../shared/enums/ingredient-category.enum';
import { IngredientLocation } from '../../../shared/enums/ingredient-location.enum';

export interface IngredientState {
  ingredients: Ingredient[];
  loading: boolean;
  loaded: boolean;
  error: any;
  filters: IngredientFilterMap;
  searchTerm: string;
  briefResults: IngredientBrief[];
  briefLoading: boolean;
}

export interface IngredientFilterMap {
  category: IngredientCategory[];
  default_location: IngredientLocation[];
  essential: boolean | null;
  commonly_disliked: boolean | null;
}

const initialState: IngredientState = {
  ingredients: [],
  loading: false,
  loaded: false,
  error: null,
  filters: {
    category: [],
    default_location: [],
    essential: null,
    commonly_disliked: null
  },
  searchTerm: '',
  briefResults: [],
  briefLoading: false,
};

export const ingredientReducer = createReducer(
  initialState,
  on(IngredientActions.loadIngredients, state => ({ ...state, loading: true })),
  on(IngredientActions.loadIngredientsSuccess, (state, { ingredients }) => ({
    ...state,
    loading: false,
    loaded: true,
    ingredients
  })),
  on(IngredientActions.loadIngredientsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(IngredientActions.addIngredient, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(IngredientActions.addIngredientSuccess, (state, { ingredient }) => ({
    ...state,
    ingredients: [...state.ingredients, ingredient],
    loading: false,
    error: null
  })),
  on(IngredientActions.addIngredientFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(IngredientActions.updateIngredient, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(IngredientActions.updateIngredientSuccess, (state, { ingredient }) => ({
    ...state,
    ingredients: state.ingredients.map(i => i.ingredient_id === ingredient.ingredient_id ? ingredient : i),
    loading: false,
    error: null
  })),
  on(IngredientActions.updateIngredientFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(IngredientActions.deleteIngredient, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(IngredientActions.deleteIngredientSuccess, (state, { ingredientId }) => ({
    ...state,
    ingredients: state.ingredients.filter(i => i.ingredient_id !== ingredientId),
    loading: false,
    error: null
  })),
  on(IngredientActions.deleteIngredientFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(IngredientActions.setIngredientFilters, (state, { filters }) => ({
    ...state,
    filters
  })),
  on(IngredientActions.clearIngredientFilters, (state) => ({
    ...state,
    filters: {
      category: [],
      default_location: [],
      essential: null,
      commonly_disliked: null
    }
  })),
  on(IngredientActions.updateIngredientSearchTerm, (state, { term }) => ({
    ...state,
    searchTerm: term
  })),
  on(IngredientActions.resetIngredientFilters, (state) => ({
    ...state,
    filters: {
      category: [],
      default_location: [],
      essential: null,
      commonly_disliked: null
    }
  })),
  on(IngredientActions.resetIngredientSearchTerm, (state) => ({
    ...state,
    searchTerm: ''
  })),
  on(IngredientActions.updateIngredientSearchTerm, (state, { term }) => ({
  ...state,
  searchTerm: term,
  briefResults: term.trim().length < 3 ? [] : state.briefResults
  })),
  on(IngredientActions.searchIngredientsBrief, (state) => ({
    ...state,
    briefLoading: true
  })),
  on(IngredientActions.searchIngredientsBriefSuccess, (state, { results }) => ({
    ...state,
    briefLoading: false,
    briefResults: results
  })),
  on(IngredientActions.searchIngredientsBriefFailure, (state) => ({
    ...state,
    briefLoading: false
  })),
  on(IngredientActions.resetIngredientsBrief, (state) => ({
    ...state,
    briefResults: []
  })),
  on(IngredientActions.loadEssentialIngredientsBrief, (state) => ({
    ...state,
    briefLoading: true
  })),
  on(IngredientActions.loadEssentialIngredientsBriefSuccess, (state, { results }) => ({
    ...state,
    briefLoading: false,
    briefResults: results
  })),
  on(IngredientActions.loadEssentialIngredientsBriefFailure, (state) => ({
    ...state,
    briefLoading: false
  })),
  on(IngredientActions.resetIngredients, () => initialState)
);
